import type { DesignKit } from './kits';

/**
 * A kit's CSS custom properties, read back out so React Native can draw with them.
 *
 * The wizard shows a mockup of somebody's app in three kits before the app exists, and the
 * whole value of that step is that the look they pick is the look they get. Keeping a second,
 * hand-written copy of "what Editorial looks like" for the mockup would drift from the
 * stylesheet the build actually ships — and the drift would surface as a preview that lied.
 * So the `css` block stays the single source and this reads it.
 *
 * Deliberately not a CSS parser. It reads `--name: value;` declarations and nothing else,
 * because that is all these blocks contain and a general parser would be a lot of surface
 * for no extra truth.
 */
export type KitTokens = Record<string, string>;

/** What a mockup cannot be drawn without. A kit missing one of these renders as a blank card. */
export const REQUIRED_TOKENS = [
  'font-display',
  'font-body',
  'font-num',
  'display-weight',
  'display-case',
  'bg',
  'surface',
  'surface-2',
  'ink',
  'ink-muted',
  'line',
  'accent',
  'accent-ink',
  'accent-wash',
  'radius',
  'pad',
] as const;

const DECLARATION = /--([a-z0-9-]+)\s*:\s*([^;]+);/gi;

/** Light-mode tokens. The mockup is drawn in Saddle's own surface, which is light. */
export function kitTokens(kit: DesignKit): KitTokens {
  const out: KitTokens = {};
  for (const [, name, value] of kit.css.matchAll(DECLARATION)) {
    out[name] = value.trim();
  }
  return out;
}

/**
 * CSS generic keywords are not font names.
 *
 * Handed to React Native, most name nothing it can look up, and the kit falls back to the
 * system face — Console stops being monospaced, and three "different" options render as
 * three of the same thing. So each maps to a NAMED face iOS has that keeps the keyword's
 * promise: Menlo for `ui-monospace`, Georgia for `ui-serif`.
 *
 * `ui-rounded` is the exception and passes through untouched, because iOS has no rounded
 * face addressable by name: SF Rounded ships only as the hidden `.SF UI Rounded` family,
 * reachable as a system design. This used to map to "SF Pro Rounded", which no font on the
 * phone answers to, so RN drew plain SF and Soft — the kit whose whole personality is its
 * type — previewed exactly like the others. Fabric (the app runs the new architecture,
 * `newArchEnabled` in app.json) resolves the keyword itself, to
 * UIFontDescriptorSystemDesignRounded at the requested weight: the same face Safari gives
 * the built app for `ui-rounded`, so the preview and the build agree. Android and the old
 * architecture do not know the keyword and draw their default face, which is all
 * "SF Pro Rounded" ever got there either. kitTokens.test.ts reads RN's resolver to check
 * the keyword is still mapped.
 */
const GENERIC: Record<string, string> = {
  'ui-rounded': 'ui-rounded',
  'ui-monospace': 'Menlo',
  'ui-serif': 'Georgia',
  'ui-sans-serif': 'Helvetica Neue',
  '-apple-system': 'Helvetica Neue',
  'system-ui': 'Helvetica Neue',
  serif: 'Georgia',
  'sans-serif': 'Helvetica Neue',
  monospace: 'Menlo',
};

/**
 * The one family React Native should be given, out of a CSS stack.
 *
 * A stack is a list of hopes and RN takes a single name, so this walks the list and returns
 * the first entry that names a real face — mapping the generics above, and skipping
 * "SF Mono" and "SF Pro Rounded", which are not addressable by name on iOS even though the
 * CSS asks for them. Returned, either one silently draws the plain system face.
 */
const UNADDRESSABLE = new Set(['sf mono', 'sf pro', 'sf pro text', 'sf pro display', 'sf pro rounded']);

export function rnFontFamily(stack: string | undefined): string {
  if (!stack) return 'System';
  for (const raw of stack.split(',')) {
    const name = raw.trim().replace(/^["']|["']$/g, '');
    if (!name) continue;
    const mapped = GENERIC[name.toLowerCase()];
    if (mapped) {
      if (!UNADDRESSABLE.has(mapped.toLowerCase())) return mapped;
      continue;
    }
    if (UNADDRESSABLE.has(name.toLowerCase())) continue;
    return name;
  }
  return 'System';
}

/** `--display-weight: 700` → the string React Native's `fontWeight` wants. */
export function rnFontWeight(value: string | undefined): '400' | '600' | '700' {
  const n = Number((value ?? '').trim());
  if (n >= 700) return '700';
  if (n >= 600) return '600';
  return '400';
}

/** `--radius: 14px` → 14. Falls back rather than producing NaN, which crashes a style. */
export function px(value: string | undefined, fallback: number): number {
  const n = Number.parseFloat((value ?? '').trim());
  return Number.isFinite(n) ? n : fallback;
}
